<script>
    import { createEventDispatcher, onMount, getContext } from 'svelte';
    import { Language } from "@Store";
    import translations from "../utils/translations.js";
    const dispatch = createEventDispatcher();

    // Build a lookup map for translations (English -> French)
    const translationMap = {};
    translations.forEach(item => {
        if (item.eng && item.fr) {
            translationMap[item.eng] = item.fr;
        }
    });

    // Translate text for display only (visual change, not saved)
    // Takes language as parameter for Svelte reactivity
    function translate(text, lang) {
        if (lang === 'fr' && text && translationMap[text]) {
            return translationMap[text];
        }
        return text;
    }

    // Reactive shorthand for current language
    $: t = (text) => translate(text, $Language);

    export let question;
    export let policy;
    export let highlight = false;
    export let required = false;
    export let remove_options = [];
    export let title = ''

    const CONTEXT = getContext('CONTEXT');
    let savingQuestions = CONTEXT?.savingQuestions;

    $: rawHeaderText = ($Language === 'fr' && question.title) ? question.title : question.question;
    $: displayHeaderText = rawHeaderText ? rawHeaderText.replace(/\s*\((CGL & Property|CGL)\)/gi, '') : '';

    let answer = policy.answers?.[question.id]?.text_answer || '';
    let debounceTimer;

    // Address autocomplete
    let addressInput;

    // Track selected tile choices for multi-select
    let selectedTileIds = [];

    // Custom "other" text for tiles
    let customOtherText = '';

    // Initialize custom other text from answer if subtitle is ALLOW OTHER
    $: if (question.type === "Tiles" && question.subtitle === 'ALLOW OTHER') {
        const currentAnswer = policy.answers?.[question.id];
        // If there's a text answer and no choice selected, it's custom text
        if (currentAnswer?.text_answer && !currentAnswer?.choice) {
            customOtherText = currentAnswer.text_answer;
        }
    }

    // Initialize selected tiles from answer
    $: if (question.type === "Tiles" && question.allow_multiple_answers) {
        const raw = policy.answers?.[question.id]?.other || '';
        selectedTileIds = raw.split(',').map(s => s.trim()).filter(Boolean);
    }

    // Collection state
    let collectionData = null;

    function blankInner(iq) {
        return { question: iq.uuid, text_answer: '', choice: null, other: '' };
    }

    function initCollection() {
        // Try to load from policy
        try {
            const saved = policy.answers?.[question.id]?.text_answer;
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.collections === 'number' && parsed.answers) {
                    collectionData = parsed;
                }
            }
        } catch (e) {}

        if (!collectionData) {
            collectionData = { collections: 1, answers: {} };
        }
        ensureCollectionShape();
    }

    function ensureCollectionShape() {
        const n = Math.max(1, parseInt(collectionData.collections) || 1);
        collectionData.collections = n;

        (question.inner_questions || []).forEach(iq => {
            if (!collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid] = [];
            for (let i = 0; i < n; i++) {
                if (!collectionData.answers[iq.uuid][i]) {
                    collectionData.answers[iq.uuid][i] = blankInner(iq);
                } else {
                    const a = collectionData.answers[iq.uuid][i];
                    if (a.question == null) a.question = iq.uuid;
                    if (a.text_answer == null) a.text_answer = '';
                    if (a.choice == null) a.choice = null;
                    if (a.other == null) a.other = '';
                }
            }
            if (collectionData.answers[iq.uuid].length > n) {
                collectionData.answers[iq.uuid].length = n;
            }
        }); 
    }

    function addCollectionRow() {
        collectionData.collections += 1;
        const idx = collectionData.collections - 1;
        (question.inner_questions || []).forEach(iq => {
            if (!collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid] = [];
            collectionData.answers[iq.uuid][idx] = blankInner(iq);
        });
        scheduleCollectionSave();
    }

    function removeCollectionRow(index) {
        if (collectionData.collections <= 1) return;
        (question.inner_questions || []).forEach(iq => {
            if (collectionData.answers[iq.uuid]) collectionData.answers[iq.uuid].splice(index, 1);
        });
        collectionData.collections -= 1;
        scheduleCollectionSave();
    }

    function setInnerText(uuid, index, value) {
        const arr = collectionData.answers[uuid] || (collectionData.answers[uuid] = []);
        const obj = arr[index] || (arr[index] = { question: uuid, text_answer: '', choice: null, other: '' });
        obj.text_answer = value;
    }

    function onInnerInput(uuid, index, e) {
        setInnerText(uuid, index, e.target.value);
        scheduleCollectionSaveDebounced();
    }

    function onInnerInteger(uuid, index, e) {
        const v = e.target.value.replace(/\D/g, '');
        e.target.value = v;
        setInnerText(uuid, index, v);
        scheduleCollectionSaveDebounced();
    }

    function onInnerDate(uuid, index, e) {
        // Expect YYYY-MM-DD
        setInnerText(uuid, index, e.target.value);
        scheduleCollectionSaveDebounced();
    }

    function getInnerText(uuid, index) {
        return (collectionData?.answers?.[uuid]?.[index]?.text_answer) || '';
    }

    function scheduleCollectionSave() {
        const payload = { collections: collectionData.collections, answers: collectionData.answers };
        dispatch('answered', {
            questionId: question.id,
            text_answer: JSON.stringify(payload)
        });
    }

    function scheduleCollectionSaveDebounced() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            scheduleCollectionSave();
        }, 400);
    }

    // Initialize once, and refresh if server pushes a newer value
    onMount(async () => {
        if (question.type === "Collection") {
            initCollection();
        }
    });

    $: if (question.type === "Collection") {
        const server = policy.answers?.[question.id]?.text_answer || '';
        if (server && (!collectionData || collectionData._server !== server)) {
            try {
                collectionData = JSON.parse(server);
                collectionData._server = server;
                ensureCollectionShape();
            } catch (e) {}
        }
    }

    function loadScriptOnce(id, src) {
        return new Promise((resolve, reject) => {
            if (document.getElementById(id)) return resolve();
            const s = document.createElement('script');
            s.id = id;
            s.src = src;
            s.async = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error(`Failed to load ${src}`));
            document.head.appendChild(s);
        });
    }

    async function ensureGooglePlacesLoaded() {
        if (window.google && window.google.maps && window.google.maps.places) return;
        if (!window.google_maps_api_key) {
            throw new Error('Missing window.google_maps_api_key');
        }
        const src = `https://maps.googleapis.com/maps/api/js?key=${window.google_maps_api_key}&libraries=places`;
        await loadScriptOnce('gmaps-places', src);
    }

    function setupAddressAutocomplete() {
        if (!addressInput) return;
        const options = {
            componentRestrictions: { country: policy.countries || [] }
        };
        const autocomplete = new window.google.maps.places.Autocomplete(addressInput, options);

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const formatted = place?.formatted_address || '';
            addressInput.value = formatted;
            answer = formatted;

            dispatch('answered', {
                questionId: question.id,
                text_answer: formatted
            });
        });
    }

    function handleAnswer(e) {
        answer = e.target.value;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (question.type === "Text") {
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: answer
                });
            }
        }, 500);
    }

    function handleBooleanAnswer(value) {
        answer = value;
        dispatch('answered', {
            questionId: question.id,
            text_answer: value
        });
    }

    async function handleCalendarAnswer(e) {
        answer = e.target.value;
    
        // Dispatch the answer first
        dispatch('answered', {
            questionId: question.id,
            text_answer: answer
        });
        
        // If this is an effective date question, wait a bit for the answer to save
        // then dispatch the date update
        if (question.effective_date) {
            // Wait for the batched save to complete (400ms debounce + request time)
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Calculate end date: add 365 days to the start date
            const startDate = new Date(answer);
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 365);
            
            // Format dates as YYYY-MM-DD
            const formattedEndDate = endDate.toISOString().split('T')[0];
            
            dispatch('updatedEffectiveDate', {
                start_date: answer,
                end_date: formattedEndDate
            });
        }
    }
    function handleTileAnswer(choice) {
        // Clear custom other text when selecting a tile
        customOtherText = '';
        
        if (question.allow_multiple_answers) {
            // Multi-select: toggle the choice
            const choiceIdStr = String(choice.id);
            const index = selectedTileIds.indexOf(choiceIdStr);
            
            if (index > -1) {
                // Deselect
                selectedTileIds = selectedTileIds.filter(id => id !== choiceIdStr);
            } else {
                // Select
                selectedTileIds = [...selectedTileIds, choiceIdStr];
            }

            // Build labels from selected IDs
            const labelById = {};
            question.questionchoice_set.forEach(c => {
                labelById[String(c.id)] = c.answer;
            });
            const labels = selectedTileIds.map(id => labelById[id]).filter(Boolean);

            dispatch('answered', {
                questionId: question.id,
                text_answer: labels.join(', '),
                choice: selectedTileIds.length ? selectedTileIds[0] : null,
                choices: selectedTileIds,
                other: selectedTileIds.join(',')
            });
        } else {
            // Single select
            const isCurrentlySelected = policy.answers?.[question.id]?.choice === choice.id;
            const isRequired = question.required || required;
            
            // Allow deselection if not required and already selected
            if (isCurrentlySelected && !isRequired) {
                answer = '';
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: '',
                    choice: null
                });
            } else {
                answer = choice.answer;
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: choice.answer,
                    choice: choice.id
                });
            }
        }
    }

    function handleIntegerAnswer(e) {
        // Only allow digits
        const value = e.target.value.replace(/\D/g, '');
        e.target.value = value;
        answer = value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: value
            });
        }, 500);
    }

    function handleCustomOtherText(e) {
        customOtherText = e.target.value;
        
        // Clear tile selections
        selectedTileIds = [];
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (customOtherText.trim()) {
                dispatch('answered', {
                    questionId: question.id,
                    text_answer: customOtherText.trim(),
                    choice: null,
                    choices: [],
                    other: ''
                });
            }
        }, 500);
    }

    // Check if a tile choice is selected
    function isTileSelected(choiceId) {
        if (question.allow_multiple_answers) {
            return selectedTileIds.includes(String(choiceId));
        }
        return policy.answers?.[question.id]?.choice === choiceId;
    }

    function formatDate(dateString) {
        if (!dateString) return '';
        // Parse date string as local date to avoid timezone issues
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    onMount(async () => {
        if (question.type === "Address") {
            try {
                await ensureGooglePlacesLoaded();
                setupAddressAutocomplete();
            } catch (e) {
                console.error('Google Places failed to load:', e);
            }
        }
    });

    // Phone number formatting
    function formatPhoneNumber(value) {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX for North American numbers
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else if (digits.length <= 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        } else {
            // Handle longer numbers (international)
            return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
        }
    }

    // Email validation
    function isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    // Phone number handler
    function handlePhoneAnswer(e) {
        const rawValue = e.target.value;
        const formatted = formatPhoneNumber(rawValue);
        e.target.value = formatted;
        answer = formatted;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: formatted
            });
        }, 500);
    }

    // Email handler with validation
    function handleEmailAnswer(e) {
        answer = e.target.value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: answer
            });
        }, 500);
    }

    // Year validation and handler
    function isValidYear(year) {
        const yearNum = parseInt(year);
        return yearNum >= 1900 && yearNum <= 2025;
    }

    function handleYearAnswer(e) {
        // Only allow digits and limit to 4 characters
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 4) {
            value = value.slice(0, 4);
        }
        e.target.value = value;
        answer = value;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: value
            });
        }, 500);
    }

    // Currency formatting and handler
    function formatCurrency(value) {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        
        if (!digits) return '';
        
        // Convert to number and format with commas
        const number = parseInt(digits, 10);
        return number.toLocaleString('en-US');
    }

    function handleParagraphAnswer(e) {
        answer = e.target.value;

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: answer
            });
        }, 500);
    }

    function handleCurrencyAnswer(e) {
        const rawValue = e.target.value;
        const formatted = formatCurrency(rawValue);
        e.target.value = formatted;
        
        // Store the raw number without formatting
        const rawNumber = rawValue.replace(/\D/g, '');
        answer = rawNumber;
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            dispatch('answered', {
                questionId: question.id,
                text_answer: rawNumber
            });
        }, 500);
    }

    // Add this helper to fetch the API key
    async function fetchCanadaPostApiKey(policyUuid) {
        if (window.canada_post_api_key) {
            return window.canada_post_api_key;
        }
        
        try {
            const response = await fetch(`/api/policy/policy/${policyUuid}/post_address_key/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')?.value || ''
                }
            });
            const data = await response.json();
            if (data.api_key) {
                window.canada_post_api_key = data.api_key;
                return data.api_key;
            }
        } catch (e) {
            console.error('Failed to fetch Canada Post API key:', e);
        }
        return null;
    }

    // Add this helper to load the Canada Post script
    function loadCanadaPostScript(apiKey) {
        return new Promise((resolve, reject) => {
            if (typeof pca !== 'undefined') {
                resolve();
                return;
            }
            
            if (document.getElementById('canada-post-script')) {
                // Script already loading, wait for it
                const checkPca = setInterval(() => {
                    if (typeof pca !== 'undefined') {
                        clearInterval(checkPca);
                        resolve();
                    }
                }, 100);
                return;
            }
            
            // Load CSS
            const css = document.createElement('link');
            css.rel = 'stylesheet';
            css.href = 'https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css';
            document.head.appendChild(css);
            
            // Load JS
            const script = document.createElement('script');
            script.id = 'canada-post-script';
            script.src = `https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js?key=${apiKey}`;
            script.onload = () => {
                console.log('Canada Post script loaded');
                resolve();
            };
            script.onerror = () => reject(new Error('Failed to load Canada Post script'));
            document.head.appendChild(script);
        });
    }

    // Update the canadaPostAutocomplete action
    function canadaPostAutocomplete(node, { onSelect, policy }) {
        let control = null;
        
        async function init() {
            console.log('canadaPostAutocomplete initializing...');
            
            // Only proceed if Canada Post is enabled
            if (!policy?.enable_canada_post_api) {
                console.log('Canada Post API not enabled, skipping');
                return;
            }
            
            // Fetch API key if not already available
            const apiKey = await fetchCanadaPostApiKey(policy.uuid);
            if (!apiKey) {
                console.warn('No Canada Post API key available');
                return;
            }
            
            console.log('Got API key, loading script...');
            
            // Load the Canada Post script
            try {
                await loadCanadaPostScript(apiKey);
            } catch (e) {
                console.error('Failed to load Canada Post script:', e);
                return;
            }
            
            console.log('Script loaded, initializing autocomplete...');
            
            // Initialize the autocomplete
            const fields = [
                { element: node, field: '', mode: pca.fieldMode.SEARCH },
            ];
            
            const options = {
                key: apiKey,
                country: 'CA',
                bar: { visible: false }
            };
            
            control = new pca.Address(fields, options);
            
            control.listen('populate', function(address) {
                const formatted = address.Label.replaceAll('\n', ', ');
                const addressData = {
                    full_address: formatted,
                    address: address.Line1 || '',
                    city: address.City || '',
                    state: address.ProvinceName || '',
                    postal_code: address.PostalCode || '',
                    country: address.CountryName || 'Canada'
                };
                onSelect(addressData);
            });
            
            console.log('Canada Post autocomplete initialized successfully');
        }
        
        // Start initialization
        init();
        
        return {
            destroy() {
                // Cleanup if needed
                control = null;
            }
        };
    }

    function getCanadaPostAddress(ans) {
        if (!ans?.text_answer) return null;
        try {
            return typeof ans.text_answer === 'string' 
                ? JSON.parse(ans.text_answer) 
                : ans.text_answer;
        } catch {
            return { full_address: ans.text_answer };
        }
    }

    // Get display value for the search input
    function getCanadaPostDisplayValue(ans) {
        const addr = getCanadaPostAddress(ans);
        return addr?.full_address || '';
    }
</script>

<div class="question-wrapper" class:highlight-required={highlight}>
    <div class="question-header">
        <h3>
            {#if question.question.toLowerCase().includes('do you require cyber insurance?')}
            <!-- show above question text:  -->
                <span style='font-size: 1.2rem; font-weight: 600; margin-top: 1rem;'>Your medical malpractice insurance package includes $50,000 limit for cyber cover including privacy breach.</span><br>
            {/if}
            {#if question.question.toLowerCase().includes('medical malpractice policy currently includes the following legal services:')}
                <div class="legal-services-info">
                    <p class="info-header">Your Medical Malpractice Policy currently includes the following legal services:</p>
                    <ul class="services-list">
                        <li>Legal Helpline</li>
                        <li>Simple Legal Letter Drafting</li>
                        <li>Document Review</li>
                        <li>Document Centre</li>
                    </ul>
                </div>
                <span style='font-size: 1.2rem; font-weight: 600; margin-top: 1rem;'>Do you require additional Legal Expense Insurance ($200,000 limit) for $119?</span>
                <div class="legal-services-info" style="margin-bottom: 0, margin-top: 1rem">
                    <p class="info-header">Legal Expense Insurance provides the following coverages:</p>
                    <ul class="services-list get-left">
                        <li><strong>Legal Defense</strong> - Providing legal assistance in a criminal prosecution or defending against an occupational health & safety investigation</li>
                        <li><strong>Statutory License Protection</strong> - Revocation, suspension, or non-renewal of an operating or business license</li>
                        <li><strong>Property Protection</strong> - Pursuing a claim for nuisance, trespass, or physical damage to business property. Minimum amount in dispute is $1,000</li>
                        <li><strong>Bodily Injury</strong> - Pursuing a claim by an employee for a work-related injury</li>
                        <li><strong>Tax Protection</strong> - Responding to a tax audit or appealing a Canada Revenue Agency (CRA) decision</li>
                    </ul>
                </div>
            {:else}
                { title || displayHeaderText}
            {/if}
            {#if question.required || required}
                <span class="required-indicator" title="Required">*</span>
            {/if}
        </h3>
        {#if question.help_text}
            <div class="help-icon-wrapper">
                <svg class="help-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M12 16v.01M12 13a1 1 0 0 1 1-1 2 2 0 1 0-2-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <div class="help-tooltip">
                    {t(question.help_text)}
                </div>
            </div>
        {/if}
    </div>
    
    {#if savingQuestions && $savingQuestions?.[question.id]}
        <div class="saving-indicator">Saving…</div>
    {/if}
    
    {#if question.type === "Text"}
        {#if question.questionchoice_set && question.questionchoice_set.length > 0}
            <!-- Dropdown Select for Text with Choices -->
            <select 
                name="question-{question.id}" 
                value={answer} 
                on:change={handleAnswer}
                class="select-input"
                autocomplete="new-password"
            >
                <option value="" disabled selected={!answer}>
                    {question.placeholder_text || 'Select an option...'}
                </option>
                {#each question.questionchoice_set.sort((a, b) => a.position - b.position) as choice (choice.id)}
                    <option value={choice.answer} selected={answer === choice.answer}>
                        {t(choice.answer)}
                    </option>
                {/each}
            </select>
        {:else if question.question.toLowerCase().includes('phone') || question.question.toLowerCase().includes('telephone')}
                <!-- Phone Number Input -->
            <input 
                type="tel" 
                name="question-{question.id}" 
                value={answer} 
                autocomplete="off"
                data-lpignore="true"
                data-form-type="other"
                on:input={handlePhoneAnswer}
                on:change={handlePhoneAnswer}
                placeholder={question.placeholder_text || '(555) 123-4567'}
                class="phone-input"
            />
        {:else if question.question.toLowerCase().includes('email')}
            <!-- Email Input -->
            <div class="email-wrapper">
                <input 
                    type="email" 
                    name="question-{question.id}" 
                    value={answer} 
                    on:input={handleEmailAnswer}
                    on:change={handleEmailAnswer}
                    placeholder={question.placeholder_text || 'your@email.com'}
                    class="email-input"
                    autocomplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    class:invalid={answer && !isValidEmail(answer)}
                />
                {#if answer && !isValidEmail(answer)}
                    <div class="email-error">Please enter a valid email address</div>
                {/if}
            </div>
        {:else}
            <!-- Regular Text Input -->
            <input 
                type="text" 
                name="question-{question.id}" 
                value={answer} 
                on:input={handleAnswer}
                on:change={handleAnswer}
                autocomplete="off"
                data-lpignore="true"
                data-form-type="other"
                placeholder={question.placeholder_text || ''}
            />
        {/if}
    {/if}
    
    {#if question.type === "Boolean"}
        <div class="boolean-options">
            <button 
                class="boolean-button" 
                class:selected={answer === 'yes'}
                on:click={() => handleBooleanAnswer('yes')}
            >
                <div class="button-content">
                    <!-- <svg class="checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg> -->
                    <span>{t("Yes")}</span>
                </div>
            </button>
            
            <button 
                class="boolean-button" 
                class:selected={answer === 'no'}
                on:click={() => handleBooleanAnswer('no')}
            >
                <div class="button-content">
                    <!-- <svg class="checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg> -->
                    <span>{t("No")}</span>
                </div>
            </button>
        </div>
    {/if}

    {#if question.type === "Calendar"}
        <div class="calendar-wrapper">
            <div class="calendar-display">
                <svg class="calendar-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
                    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="2"/>
                    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                {#if question.question.toLowerCase().includes('member since:')}
                    <input 
                        type="date" 
                        class="calendar-input"
                        name="question-{question.id}" 
                        value={answer} 
                        max={new Date().toISOString().split('T')[0]}
                        on:change={handleCalendarAnswer}
                    />
                {:else if question.question.toLowerCase().includes('desired effective date')}
                <input 
                        type="date" 
                        class="calendar-input"
                        name="question-{question.id}" 
                        value={answer} 
                        min={new Date().toISOString().split('T')[0]}
                        on:change={handleCalendarAnswer}
                        autocomplete="off"
                    />
                {:else}
                    <input
                        type="date"
                        class="calendar-input"
                        name="question-{question.id}"
                        value={answer}
                        min={question.effective_date ? new Date().toISOString().split('T')[0] : undefined}
                        on:change={handleCalendarAnswer}
                    />
                {/if}
                <span class="calendar-display-text">
                    {answer ? formatDate(answer) : 'Select a date'}
                </span>
            </div>
        </div>
    {/if}

    {#if question.type === "Address"}
        <input
            type="text"
            class="address-input"
            name="question-{question.id}"
            placeholder="Start typing your address..."
            bind:this={addressInput}
            value={answer}
            autocomplete="new-password"
            readonly
            onfocus="this.removeAttribute('readonly');"
        />
    {/if}
    {#if question.type === "CanadaPostAddress"}
        <div class="canada-post-address-wrapper">
            <div class="canada-post-search">
                <input 
                    type="text" 
                    class="canada-post-address-input"
                    placeholder="Start typing an address then select it"
                    value={getCanadaPostDisplayValue(policy.answers?.[question.id])}
                    autocomplete="off"
                    use:canadaPostAutocomplete={{
                        policy: policy,
                        onSelect: (addressData) => {
                            answer = JSON.stringify(addressData);
                            
                            // Dispatch to save
                            dispatch('answered', {
                                questionId: question.id,
                                text_answer: JSON.stringify(addressData)
                            });
                        }
                    }}
                    on:input={(e) => {
                        // Allow manual typing
                        answer = e.target.value;
                    }}
                />
            </div>

            
            <!-- Expanded address fields (optional - toggle with a checkbox or always show) -->
            <!-- {#if getCanadaPostAddress(answer)}
                {@const addr = getCanadaPostAddress(answer)}
                <div class="canada-post-fields">
                    <div class="address-field">
                        <label>Address</label>
                        <input type="text" value={addr.address || ''} readonly />
                    </div>
                    <div class="address-field">
                        <label>City</label>
                        <input type="text" value={addr.city || ''} readonly />
                    </div>
                    <div class="address-field-row">
                        <div class="address-field">
                            <label>Province</label>
                            <input type="text" value={addr.state || ''} readonly />
                        </div>
                        <div class="address-field">
                            <label>Postal Code</label>
                            <input type="text" value={addr.postal_code || ''} readonly />
                        </div>
                    </div>
                    <div class="address-field">
                        <label>Country</label>
                        <input type="text" value={addr.country || ''} readonly />
                    </div>
                </div>
            {/if} -->
        </div>
    {/if}

    {#if question.type === "Integer" || question.type === "Number"}
        {#if question.question.toLowerCase().includes('year established')}
            <div class="year-wrapper">
                <input 
                    type="text" 
                    inputmode="numeric"
                    pattern="[0-9]*"
                    name="question-{question.id}" 
                    value={answer} 
                    on:input={handleYearAnswer}
                    placeholder={question.placeholder_text || 'YYYY'}
                    class="year-input"
                    class:invalid={answer && answer.length === 4 && !isValidYear(answer)}
                    maxlength="4"
                />
                {#if answer && answer.length === 4 && !isValidYear(answer)}
                    <div class="year-error">Please enter a year between 1900 and 2025</div>
                {/if}
            </div>
        {:else}
            <div class="number-wrapper">
                <input 
                    type="text" 
                    inputmode="numeric"
                    pattern="[0-9]*"
                    class="number-input"
                    name="question-{question.id}" 
                    value={answer} 
                    on:input={handleIntegerAnswer}
                    placeholder={question.placeholder_text || 'Enter a number'}
                />
            </div>
        {/if}
    {/if}

    {#if question.type === "Tiles"}
        <div class="tiles-wrapper">
            {#each question.questionchoice_set.sort((a, b) => a.position - b.position).filter((choice, index) => !remove_options.includes(index)) as choice (choice.id)} 
                <button 
                        class="tile-button" 
                        class:selected={isTileSelected(choice.id)}
                        on:click={() => handleTileAnswer(choice)}
                    >
                        <div class="tile-content">
                            {#if choice.icon}
                                <div class="tile-icon">
                                    {@html choice.icon}
                                </div>
                            {/if}
                            <span class="tile-text">{t(choice.answer)}</span>
                        </div>
                    </button>
            {/each}
        </div>
        
        {#if question.subtitle === 'ALLOW OTHER'}
            <div class="custom-other-wrapper">
                <div class="divider">
                    <span>OR</span>
                </div>
                <input 
                    type="text" 
                    class="custom-other-input"
                    placeholder="Enter your own answer..."
                    value={customOtherText}
                    on:input={handleCustomOtherText}
                />
            </div>
        {/if}
    {/if}

    {#if question.type === "Collection"}
        <div class="collection-wrapper">
            {#if collectionData}
                {#each Array(collectionData.collections) as _, idx}
                    <div class="collection-row">
                        <div class="collection-row-header">
                            <div class="collection-row-title">Entry {idx + 1}</div>
                            {#if collectionData.collections > 1}
                                <button class="remove-row" on:click={() => removeCollectionRow(idx)}>Remove</button>
                            {/if}
                        </div>
                        <div class="collection-row-body">
                            {#each (question.inner_questions || []).sort((a, b) => a.position - b.position) as iq (iq.uuid)}
                                <div class="inner-question">
                                    <label>{t(iq.question_text)}</label>
                                    {#if iq.type === 'Text'}
                                        <input type="text" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInput(iq.uuid, idx, e)} />
                                    {:else if iq.type === 'Integer' || iq.type === 'Number'}
                                        <input type="text" inputmode="numeric" pattern="[0-9]*" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInteger(iq.uuid, idx, e)} />
                                    {:else if iq.type && iq.type.startsWith('Calendar')}
                                        <input type="date" value={getInnerText(iq.uuid, idx)} on:change={(e) => onInnerDate(iq.uuid, idx, e)} />
                                    {:else}
                                        <input type="text" value={getInnerText(iq.uuid, idx)} on:input={(e) => onInnerInput(iq.uuid, idx, e)} />
                                    {/if}
                                </div>
                            {/each}
                        </div>
                    </div>
                {/each}
                <div class="collection-actions">
                    <button class="add-row" on:click={addCollectionRow}>{question.placeholder_text || '+ Add another'}</button>
                </div>
            {/if}
        </div>
    {/if}

    {#if question.type === "Currency"}
        <div class="currency-wrapper">
            <div class="currency-input-container">
                <span class="currency-symbol">$</span>
                <input
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9,]*"
                    class="currency-input"
                    name="question-{question.id}"
                    value={formatCurrency(answer)}
                    on:input={handleCurrencyAnswer}
                    placeholder={question.placeholder_text}
                />
            </div>
        </div>
    {/if}

    {#if question.type === "Paragraph"}
        <div class="paragraph-wrapper">
            <textarea
                class="paragraph-input"
                name="question-{question.id}"
                value={answer}
                on:input={handleParagraphAnswer}
                placeholder={question.placeholder_text || 'Enter your response...'}
                rows="6"
            ></textarea>
            {#if answer}
                <div class="character-count">
                    {answer.length} characters
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .question-wrapper.highlight-required {
    animation: shake 0.5s ease;
    }

    .question-wrapper.highlight-required h3 {
        color: var(--red);
    }

    .question-wrapper.highlight-required input,
    .question-wrapper.highlight-required .boolean-button,
    .question-wrapper.highlight-required .calendar-display,
    .question-wrapper.highlight-required .tile-button {
        border-color: var(--red) !important;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
    }

    .question-wrapper {
        text-align: center;
        padding: 2rem 1.5rem;
        padding-bottom: 0;
        max-width: 500px;
        margin: 0 auto;
    }

    /* Question Header with Help Icon */
    .question-header {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
    }

    h3 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #1d1d1f;
        margin: 0;
        line-height: 1.4;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .required-indicator {
        color: var(--red);
        font-size: 1.375rem;
        font-weight: 700;
        margin-left: 0.25rem;
        vertical-align: super;
        line-height: 0;
        display: inline-block;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: scale(0.8);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }

    .help-icon-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        cursor: help;
    }

    .help-icon {
        width: 20px;
        height: 20px;
        color: #86868b;
        transition: color 0.2s ease;
    }

    .help-icon-wrapper:hover .help-icon {
        color: var(--base-green);
    }

    .help-tooltip {
        position: absolute;
        top: calc(100% + 0.5rem);
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        font-size: 0.875rem;
        line-height: 1.4;
        white-space: pre-line; 
        min-width: 200px;
        max-width: 300px;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        z-index: 1000;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .help-tooltip::before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 50%;
        z-index: 100001;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-bottom-color: rgba(0, 0, 0, 0.9);
    }

    .help-icon-wrapper:hover .help-tooltip {
        opacity: 1;
    }

    @media (max-width: 480px) {
        .help-tooltip {
            left: auto;
            right: 0;
            transform: none;
            min-width: 250px;
        }
        
        .help-tooltip::before {
            left: auto;
            right: 1rem;
            transform: none;
        }
    }

    input[type="text"] {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    input[type="text"]:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    input[type="text"]::placeholder {
        color: #86868b;
    }

    /* Override Chrome autocomplete background color */
    input[type="text"]:-webkit-autofill,
    input[type="text"]:-webkit-autofill:hover,
    input[type="text"]:-webkit-autofill:focus,
    input[type="text"]:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
        -webkit-text-fill-color: #1d1d1f !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Green tint for autocomplete hover/focus */
    input[type="text"]:-webkit-autofill:hover {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.05) inset !important;
        border-color: var(--base-green) !important;
    }

    input[type="text"]:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.08) inset !important;
        border-color: var(--base-green) !important;
    }

    .boolean-options {
        display: flex;
        gap: 1rem;
        width: 100%;
    }

    .boolean-button {
        flex: 1;
        padding: 1.25rem 1.5rem;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        position: relative;
        overflow: hidden;
    }

    .boolean-button:hover {
        border-color:  var(--base-green);
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }

    .boolean-button:active {
        transform: translateY(0);
    }

    .boolean-button.selected {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-color: var(--base-green);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }

    .button-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
    }

    .checkmark {
        width: 32px;
        height: 32px;
        color: #d2d2d7;
        transition: all 0.25s ease;
    }

    .boolean-button.selected .checkmark {
        color: #fff;
        animation: checkScale 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .boolean-button:not(.selected) .checkmark {
        opacity: 0.3;
    }

    .button-content span {
        font-size: 1.125rem;
        font-weight: 500;
        color: #1d1d1f;
        transition: color 0.25s ease;
        letter-spacing: -0.01em;
    }

    .boolean-button.selected .button-content span {
        color: #fff;
    }

    /* Calendar Styles */
    .calendar-wrapper {
        width: 100%;
    }

    .calendar-display {
        position: relative;
        width: 100%;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        box-sizing: border-box;
        padding: 1.5rem 1.25rem;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .calendar-display:hover {
        border-color: var(--base-green);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.12);
        transform: translateY(-2px);
    }

    .calendar-display:has(.calendar-input:focus) {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .calendar-icon {
        width: 28px;
        height: 28px;
        color: var(--base-green);
        flex-shrink: 0;
    }

    .calendar-input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
        z-index: 2;
    }

    .calendar-input::-webkit-calendar-picker-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        opacity: 0;
    }

    .calendar-display-text {
        font-size: 1.0625rem;
        font-weight: 500;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        flex: 1;
        text-align: left;
    }

    .calendar-display:not(:has(input[value])) .calendar-display-text,
    .calendar-display:has(input[value=""]) .calendar-display-text {
        color: #86868b;
    }

    @keyframes checkScale {
        0% {
            transform: scale(0.8);
            opacity: 0;
        }
        50% {
            transform: scale(1.1);
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }

    @media (max-width: 480px) {
        .boolean-options {
            flex-direction: column;
        }

        .boolean-button {
            width: 100%;
        }
    }

    .address-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    .address-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    /* Override Chrome autocomplete for address inputs */
    .address-input:-webkit-autofill,
    .address-input:-webkit-autofill:hover,
    .address-input:-webkit-autofill:focus,
    .address-input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px white inset !important;
        -webkit-text-fill-color: #1d1d1f !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    .address-input:-webkit-autofill:hover {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.05) inset !important;
        border-color: var(--base-green) !important;
    }

    .address-input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0 30px rgba(49, 104, 50, 0.08) inset !important;
        border-color: var(--base-green) !important;
    }

    /* Tiles Styles */
    .tiles-wrapper {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 1rem;
        width: 100%;
        margin-top: 0.5rem;
    }

    .tile-button {
        position: relative;
        padding: 1.5rem 1rem;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 16px;
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .tile-button:hover {
        border-color: var(--base-green);
        transform: translateY(-4px);
        box-shadow: 0 12px 32px rgba(49, 104, 50, 0.15);
    }

    .tile-button:active {
        transform: translateY(-2px);
    }

    .tile-button.selected {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border-color: var(--base-green);
        box-shadow: 0 12px 32px rgba(49, 104, 50, 0.3);
    }

    .tile-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        position: relative;
    }

    .tile-icon {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.25s ease;
    }

    .tile-button.selected .tile-icon {
        transform: scale(1.1);
    }

    .tile-text {
        font-size: 1rem;
        font-weight: 500;
        color: #1d1d1f;
        transition: color 0.25s ease;
        letter-spacing: -0.01em;
        text-align: center;
        line-height: 1.4;
    }

    .tile-button.selected .tile-text {
        color: #fff;
        font-weight: 600;
    }


    @media (max-width: 480px) {
        .tiles-wrapper {
            grid-template-columns: 1fr;
            gap: 0.75rem;
        }

        .tile-button {
            min-height: 100px;
            padding: 1.25rem 1rem;
        }
    }

    @media (min-width: 481px) and (max-width: 768px) {
        .tiles-wrapper {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    /* Number Input Styles */
    .number-wrapper {
        width: 100%;
    }

    .number-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: center;
        letter-spacing: -0.02em;
    }

    .number-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .number-input::placeholder {
        color: #86868b;
        font-weight: 400;
        font-size: 1.0625rem;
    }

    /* Remove spinner buttons on number inputs */
    .number-input::-webkit-outer-spin-button,
    .number-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .number-input[type=number] {
        -moz-appearance: textfield;
    }

    /* Custom Other Input for Tiles */
    .custom-other-wrapper {
        margin-top: 1.5rem;
        width: 100%;
    }

    .divider {
        display: flex;
        align-items: center;
        text-align: center;
        margin: 1rem 0;
        color: #86868b;
        font-size: 0.875rem;
        font-weight: 500;
    }

    .divider::before,
    .divider::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #d2d2d7;
    }

    .divider span {
        padding: 0 1rem;
    }

    .custom-other-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #f5f5f7;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .custom-other-input:focus {
        outline: none;
        border-color: #0071e3;
        background-color: #fff;
        box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.1);
    }

    .custom-other-input::placeholder {
        color: #86868b;
        font-style: italic;
    }

    .saving-indicator {
        margin: -0.5rem 0 0.75rem;
        color: #86868b;
        font-size: 0.875rem;
        font-weight: 500;
    }

    /* Collection Styles */
    .collection-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
        margin-top: 1rem;
    }

    .collection-row {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        padding: 1.5rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .collection-row:hover {
        box-shadow: 0 8px 32px rgba(0, 113, 227, 0.08);
        border-color: #d2d2d7;
    }

    .collection-row-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.25rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #e9ecef;
    }

    .collection-row-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1d1d1f;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .remove-row {
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        padding: 0.5rem 1rem;
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--red);
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .remove-row:hover {
        border-color: var(--red);
        background: var(--red);
        color: #fff;
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(219, 93, 93, 0.15);
    }

    .remove-row:active {
        transform: translateY(0);
    }

    .add-row {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 2rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 600;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        letter-spacing: -0.01em;
        box-shadow: 0 4px 16px rgba(49, 104, 50, 0.2);
    }

    .add-row:hover {
        background: linear-gradient(135deg, var(--base-green-light) 0%, var(--base-green-lightest) 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 28px rgba(49, 104, 50, 0.3);
    }

    .add-row:active {
        transform: translateY(0);
    }

    .collection-row-body {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .inner-question {
        display: flex;
        flex-direction: column;
    }

    .inner-question label {
        display: block;
        text-align: left;
        font-size: 1rem;
        margin-bottom: 0.5rem;
        color: #1d1d1f;
        font-weight: 600;
        letter-spacing: -0.01em;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .inner-question input[type="text"],
    .inner-question input[type="date"] {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .inner-question input[type="text"]:focus,
    .inner-question input[type="date"]:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .inner-question input[type="text"]::placeholder {
        color: #86868b;
    }

    .collection-actions {
        display: flex;
        justify-content: center;
        margin-top: 0.5rem;
    }

    @media (max-width: 480px) {
        .collection-row {
            padding: 1.25rem;
            border-radius: 16px;
        }

        .collection-row-title {
            font-size: 1rem;
        }

        .remove-row {
            padding: 0.4rem 0.75rem;
            font-size: 0.8125rem;
        }

        .add-row {
            padding: 0.75rem 1.5rem;
            font-size: 0.9375rem;
        }

        .inner-question label {
            font-size: 0.9375rem;
        }

        .inner-question input[type="text"],
        .inner-question input[type="date"] {
            padding: 0.75rem 0.875rem;
            font-size: 1rem;
        }
    }

    @media (min-width: 481px) and (max-width: 768px) {
        .collection-row-body {
            gap: 0.875rem;
        }
    }

    /* Phone Input Styles */
    .phone-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        letter-spacing: 0.02em;
    }

    .phone-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    /* Email Input Styles */
    .email-wrapper {
        width: 100%;
    }

    .email-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 1px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    .email-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .email-input.invalid {
        border-color: var(--red);
        box-shadow: 0 0 0 4px rgba(172, 57, 49, 0.1);
    }

    .email-error {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: var(--red);
        font-weight: 500;
        text-align: left;
    }

    /* Year Input Styles */
    .year-wrapper {
        width: 100%;
    }

    .year-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: center;
        letter-spacing: 0.1em;
    }

    .year-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .year-input.invalid {
        border-color: var(--red);
        box-shadow: 0 0 0 4px rgba(172, 57, 49, 0.1);
    }

    .year-input::placeholder {
        color: #86868b;
        font-weight: 400;
        /* letter-spacing: 0.05em; */
    }

    .year-error {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: var(--red);
        font-weight: 500;
        text-align: center;
    }

    /* Currency Input Styles */
    .currency-wrapper {
        width: 100%;
    }

    .currency-input-container {
        position: relative;
        display: flex;
        align-items: center;
        background: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        transition: all 0.2s ease;
        overflow: hidden;
    }

    .currency-input-container:focus-within {
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .currency-symbol {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.875rem 0 0.875rem 1rem;
        font-size: 1.375rem;
        font-weight: 600;
        color: var(--base-green);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        user-select: none;
    }

    .currency-input {
        flex: 1;
        padding: 0.875rem 1rem 0.875rem 0.5rem;
        font-size: 1.375rem;
        font-weight: 500;
        color: #1d1d1f;
        background-color: transparent;
        border: none !important;
        box-shadow: none !important;
        box-sizing: border-box;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        text-align: left;
        letter-spacing: -0.02em;
    }

    .currency-input:focus {
        outline: none;
    }

    .currency-input::placeholder {
        color: #86868b;
        font-weight: 400;
        font-size: 1.0625rem;
    }

    /* Remove spinner buttons on currency inputs */
    .currency-input::-webkit-outer-spin-button,
    .currency-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    .currency-input[type=number] {
        -moz-appearance: textfield;
    }

    @media (max-width: 480px) {
        .currency-symbol {
            padding: 0.75rem 0 0.75rem 0.875rem;
            font-size: 1.25rem;
        }
        
        .currency-input {
            padding: 0.75rem 0.875rem 0.75rem 0.5rem;
            font-size: 1.25rem;
        }
    }

    .legal-services-info {
        background: linear-gradient(135deg, rgba(49, 104, 50, 0.05) 0%, rgba(227, 161, 37, 0.05) 100%);
        border-left: 4px solid var(--base-green);
        padding: 1.5rem;
        border-radius: 8px;
        margin-bottom: 1.5rem;
    }

    .info-header {
        font-weight: 600;
        color: var(--base-green);
        margin: 0 0 1rem 0;
        font-size: 1rem;
    }

    .services-list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .services-list li {
        padding: 0.5rem 0 0.5rem 1.75rem;
        position: relative;
        color: #333;
        font-size: 0.95rem;
    }

    .get-left{
        text-align: left;
    }

    .services-list li::before {
        content: "✓";
        position: absolute;
        left: 0;
        color: var(--base-green);
        font-weight: bold;
        font-size: 1.1rem;
    }

    .info-question {
        font-weight: 500;
        color: #1d1d1f;
        margin: 0;
        font-size: 1rem;
    }

    .select-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1rem;
        border: 2px solid #e5e7eb;
        border-radius: 12px;
        background: white;
        color: #1d1d1f;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        transition: all 0.2s ease;
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 1rem center;
        padding-right: 3rem;
    }

    .select-input:hover {
        border-color: #d1d5db;
    }

    .select-input:focus {
        outline: none;
        border-color: var(--base-green, #316832);
        box-shadow: 0 0 0 3px rgba(49, 104, 50, 0.1);
    }

    .select-input:disabled {
        background-color: #f9fafb;
        cursor: not-allowed;
        opacity: 0.6;
    }

    .canada-post-address-wrapper {
    width: 100%;
}

.canada-post-search {
    margin-bottom: 12px;
}

.canada-post-address-input {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
}

.canada-post-address-input:focus {
    outline: none;
    border-color: var(--primary-color, #4a90d9);
    box-shadow: 0 0 0 2px rgba(74, 144, 217, 0.1);
}

.canada-post-fields {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.address-field label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.address-field input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #fff;
    font-size: 14px;
}

.address-field-row {
    display: flex;
    gap: 12px;
}

.address-field-row .address-field {
    flex: 1;
}

    .paragraph-wrapper {
        width: 100%;
    }

    .paragraph-input {
        width: 100%;
        padding: 0.875rem 1rem;
        font-size: 1.0625rem;
        color: #1d1d1f;
        background-color: #fff;
        border: 2px solid #d2d2d7;
        border-radius: 12px;
        box-sizing: border-box;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        line-height: 1.6;
        resize: vertical;
        min-height: 120px;
    }

    .paragraph-input:focus {
        outline: none;
        border-color: var(--base-green);
        box-shadow: 0 0 0 4px rgba(49, 104, 50, 0.1);
    }

    .paragraph-input::placeholder {
        color: #86868b;
    }

    .character-count {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: #86868b;
        text-align: right;
        font-weight: 500;
    }

    @media (max-width: 480px) {
        .paragraph-input {
            padding: 0.75rem 0.875rem;
            font-size: 1rem;
            min-height: 100px;
        }
    }
</style>